'use client';

import { useEffect, useState } from 'react';
import MuxPlayer from '@/components/posts/MuxPlayer';
import { Loader2, Target, TrendingUp, Clock, Linkedin, Twitter, Youtube, Mail, Globe } from 'lucide-react';

interface VideoMetrics {
  momentCount: number;
  avgEngagement: number;
  duration: number;
  momentTypes?: { [key: string]: number };
}

interface PublicMoment {
  id: string;
  title: string;
  mux_playback_id: string | null;
  thumbnail_url: string | null;
  duration?: number;
  metrics?: VideoMetrics;
}

export default function FeaturedVideoHero() {
  const [moments, setMoments] = useState<PublicMoment[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch all moments with metrics
  useEffect(() => {
    async function fetchMoments() {
      try {
        const response = await fetch('/api/public-moments/showcase?includeMetrics=true');
        if (response.ok) {
          const data = await response.json();
          if (data.moments && data.moments.length > 0) {
            setMoments(data.moments);
          }
        }
      } catch (error) {
        console.error('Error fetching moments:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchMoments();
  }, []);

  // Auto-rotate videos every 1 minute
  useEffect(() => {
    if (moments.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % moments.length);
    }, 60000); // 60 seconds = 1 minute

    return () => clearInterval(interval);
  }, [moments.length]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  const currentMoment = moments[currentIndex];

  if (!currentMoment || !currentMoment.mux_playback_id) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-pink-900">
        <div className="text-white text-center p-8">
          <p className="text-lg font-medium">Featured Conversation</p>
          <p className="text-sm opacity-75 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    return `${mins}min`;
  };

  const metrics = currentMoment.metrics;

  return (
    <div className="w-full h-full relative">
      {/* Main video */}
      <MuxPlayer
        key={currentMoment.id}
        playbackId={currentMoment.mux_playback_id}
        streamType="on-demand"
        autoPlay={true}
        muted={true}
        loop={true}
        controls={false}
        className="w-full h-full object-cover"
        poster={currentMoment.thumbnail_url || undefined}
      />

      {/* Results Dashboard Overlay - Mobile simplified */}
      {metrics && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/85 to-transparent pt-4 md:pt-8 pb-3 md:pb-5 px-3 md:px-6 border-t border-white/10">
          {/* Metrics Row - Mobile shows 2 columns, desktop shows 3 */}
          <div className="grid grid-cols-2 lg:flex lg:flex-wrap items-center justify-center gap-3 md:gap-4 lg:gap-6 mb-3 md:mb-4">
            <div className="flex items-center gap-1.5 md:gap-2 text-white">
              <Target className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-purple-400" />
              <div>
                <span className="text-lg md:text-xl lg:text-3xl font-bold">{metrics.momentCount}</span>
                <span className="text-[10px] md:text-xs lg:text-sm ml-1 text-white/70">Moments</span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 md:gap-2 text-white">
              <TrendingUp className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-green-400" />
              <div>
                <span className="text-lg md:text-xl lg:text-3xl font-bold">{Math.round(metrics.avgEngagement * 100)}%</span>
                <span className="text-[10px] md:text-xs lg:text-sm ml-1 text-white/70">
                  <span className="lg:hidden">Score</span>
                  <span className="hidden lg:inline">Engagement</span>
                </span>
              </div>
            </div>

            <div className="col-span-2 lg:col-span-1 flex items-center justify-center gap-1.5 md:gap-2 text-white">
              <Clock className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-blue-400" />
              <div>
                <span className="text-base md:text-lg lg:text-2xl font-bold">{formatDuration(metrics.duration)}</span>
                <span className="text-xs md:text-sm mx-1 text-white/50">→</span>
                <span className="text-base md:text-lg lg:text-2xl font-bold">{metrics.momentCount}</span>
                <span className="text-[10px] md:text-xs lg:text-sm ml-1 text-white/70">Assets</span>
              </div>
            </div>
          </div>

          {/* Platform Distribution Row - Hidden on mobile */}
          <div className="hidden md:flex items-center justify-center gap-3">
            <span className="text-xs text-white/60 uppercase tracking-wide">Ready for:</span>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-default" title="LinkedIn">
                <Linkedin className="w-4 h-4 text-white" />
              </div>
              <div className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-default" title="X/Twitter">
                <Twitter className="w-4 h-4 text-white" />
              </div>
              <div className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-default" title="YouTube">
                <Youtube className="w-4 h-4 text-white" />
              </div>
              <div className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-default" title="Newsletter">
                <Mail className="w-4 h-4 text-white" />
              </div>
              <div className="w-7 h-7 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-default" title="Company Portal">
                <Globe className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
